import { PlayerEvents } from '@4tn/core-audio-player-shared';
import PlayerConnectionManager from '@players/services/player-connection-manager';
import { type ITritonPlayer } from './types';
import EventDispatcher from '@utils/event-dispatcher';
import type Media from '@api/models/Media';
import logger from '@utils/logger';
import { isIOS, isSafari } from '@utils/device';
import { isOfflineOnFirefox } from '@players/utils/browser';
import playerEventHandler from '@players/services/player-event-manager';

class ConnectionManager extends PlayerConnectionManager {
  private shouldTriggerReconnection = false;

  constructor(player: ITritonPlayer) {
    super(player);
    EventDispatcher.on(PlayerEvents.STREAM_STATUS, this.onStreamStatusChange.bind(this));
    EventDispatcher.on(PlayerEvents.STREAM_MUTE_STOP, () => {
      this.shouldTriggerReconnection = true;
    });
  }

  public async onNetworkReconnect(): Promise<void> {
    // we can't reconnect triton stream on IoS/Safari devices due to autoplay policy, so we don't try to recover it
    if (!this.shouldTriggerReconnection || isIOS() || isSafari()) {
      return;
    }
    try {
      this.sendReconnectingEvent();
      await playerEventHandler(PlayerEvents.STREAM_START, async () => {
        (this.player as ITritonPlayer).play();
      });

      this.sendReconnectEvent();
    } catch (error) {
      logger.error(error);
    }
    this.shouldTriggerReconnection = false;
  }

  private onStreamStatusChange(event: { name: string; media: Media }): void {
    if (this.isOnline() || this.shouldTriggerReconnection) {
      return;
    }
    this.handleNetworkReconnection(event);
  }

  private handleNetworkReconnection(event: { name: string; media: Media }): void {
    if (event.name === 'LIVE_STOP' && (isIOS() || isSafari())) {
      // handle buffering for IOS/Safari devices
      this.handleBufferOut();
      return;
    }

    if (event.name === 'LIVE_FAILED' && isOfflineOnFirefox()) {
      // handle buffering for firefox browser
      this.handleBufferOut();
      return;
    }

    if (event.name === 'LIVE_BUFFERING' && this.player.isPlaying) {
      // handle buffering for other devices/browsers
      this.handleBufferOut();
    }
  }

  private handleBufferOut(): void {
    void this.player.stop();
    this.shouldTriggerReconnection = true;
  }

  public onNetworkDisconnect(): void {
    if (!(this.player as ITritonPlayer).isHls) {
      return;
    }
    // we need to stop hls streams as soon as network is disconnected
    // because hls streams are often loaded in chunks which could result to unexpected behaviour while it is buffering offline
    void this.player.stop();
    this.shouldTriggerReconnection = true;
  }
}

export default ConnectionManager;
